// ===== 3. AI Inspection Recommendations Accordion Component (Responsive) =====
// src/components/AIInspectionRecommendations.tsx

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  useTheme,
  useMediaQuery,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge
} from '@mui/material';
import {
  Psychology,
  ExpandMore,
  CheckCircleOutline,
  WarningAmberOutlined
} from '@mui/icons-material';
import { InspectionRecommendationResponse } from '../../../../services/ai/aiInspectionRecommendationService';

interface AIInspectionRecommendationsProps {
  recommendations: InspectionRecommendationResponse | null;
  loading: boolean;
  error: string | null;
  selectedSubcode: any;
}

const AIInspectionRecommendations: React.FC<AIInspectionRecommendationsProps> = ({
  recommendations,
  loading,
  error,
  selectedSubcode
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // Auto-expand when recommendations are available, collapse when loading/error
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (recommendations && recommendations.recommendedInspections.length > 0) {
      setExpanded(true);
    } else if (error) {
      setExpanded(false);
    }
  }, [recommendations, error]);

  const handleAccordionChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
  };

  if (!selectedSubcode) {
    return null;
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return theme.palette.error.main;
      case 'Medium': return theme.palette.warning.main;
      case 'Low': return theme.palette.info.main;
      default: return theme.palette.grey[500];
    }
  };

  const getPhaseIcon = (phase: string) => {
    switch (phase) {
      case 'Early': return '🚧';
      case 'Mid': return '🔧';
      case 'Final': return '✅';
      default: return '📋';
    }
  };

  const getAccordionSummaryContent = () => {
    if (loading) {
      return (
        <Stack 
          direction="row" 
          alignItems="center" 
          spacing={1}
          sx={{ width: '100%', py: { xs: 0.5, sm: 1 } }}
        >
          <CircularProgress size={isMobile ? 14 : 16} />
          <Psychology sx={{ 
            color: theme.palette.primary.main, 
            fontSize: { xs: 18, sm: 20 } 
          }} />
          <Typography 
            variant="body2"
            color="primary" 
            sx={{ 
              fontWeight: 500,
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              flex: 1
            }}
          >
            AI Analyzing...
          </Typography>
        </Stack>
      );
    }

    if (error) {
      return (
        <Stack 
          direction="row" 
          alignItems="center" 
          spacing={1}
          sx={{ width: '100%', py: { xs: 0.5, sm: 1 } }}
        >
          <WarningAmberOutlined sx={{ 
            color: theme.palette.warning.main, 
            fontSize: { xs: 18, sm: 20 } 
          }} />
          <Typography 
            variant="body2"
            color="warning.main" 
            sx={{ 
              fontWeight: 500,
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              flex: 1
            }}
          >
            AI Analysis Unavailable
          </Typography>
        </Stack>
      );
    }

    if (!recommendations || recommendations.recommendedInspections.length === 0) {
      return (
        <Stack 
          direction="row" 
          alignItems="center" 
          spacing={1}
          sx={{ width: '100%', py: { xs: 0.5, sm: 1 } }}
        >
          <Psychology sx={{ 
            color: theme.palette.grey[500], 
            fontSize: { xs: 18, sm: 20 } 
          }} />
          <Typography 
            variant="body2"
            color="text.secondary" 
            sx={{ 
              fontWeight: 500,
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              flex: 1
            }}
          >
            No AI Recommendations
          </Typography>
        </Stack>
      );
    }

    // Success state with recommendations
    const highPriorityCount = recommendations.recommendedInspections.filter(i => i.priority === 'High').length;
    
    return (
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        alignItems={{ xs: 'flex-start', sm: 'center' }} 
        spacing={{ xs: 1, sm: 2 }}
        sx={{ width: '100%', py: { xs: 0.5, sm: 1 } }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Badge 
            badgeContent={recommendations.recommendedInspections.length} 
            color="success"
            sx={{
              '& .MuiBadge-badge': {
                fontSize: { xs: '0.6rem', sm: '0.75rem' },
                minWidth: { xs: 16, sm: 20 },
                height: { xs: 16, sm: 20 }
              }
            }}
          >
            <CheckCircleOutline sx={{ 
              color: theme.palette.success.main, 
              fontSize: { xs: 18, sm: 20 } 
            }} />
          </Badge>
          <Typography 
            variant="body2"
            sx={{ 
              fontWeight: 600,
              color: theme.palette.success.main,
              fontSize: { xs: '0.8rem', sm: '0.875rem' }
            }}
          >
            AI Recommendations
          </Typography>
          {highPriorityCount > 0 && (
            <Chip 
              label={`${highPriorityCount} High Priority`}
              size="small"
              color="error"
              variant="outlined"
              sx={{ 
                fontSize: { xs: '0.6rem', sm: '0.7rem' },
                height: { xs: 20, sm: 24 }
              }}
            />
          )}
        </Stack>
        
        <Stack 
          direction="row" 
          spacing={1}
          sx={{ 
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            gap: { xs: 0.5, sm: 1 }
          }}
        >
          <Chip 
            label={`${Math.round(recommendations.overallConfidence * 100)}%`}
            size="small"
            variant="outlined"
            sx={{ 
              fontSize: { xs: '0.65rem', sm: '0.7rem' },
              height: { xs: 20, sm: 24 }
            }}
          />
          <Chip 
            label={recommendations.projectPhase}
            size="small"
            color="primary"
            sx={{ 
              fontSize: { xs: '0.65rem', sm: '0.7rem' },
              height: { xs: 20, sm: 24 }
            }}
          />
        </Stack>
      </Stack>
    );
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={handleAccordionChange}
      elevation={0}
      sx={{
        mb: { xs: 1.5, sm: 2 },
        border: `1px solid ${
          loading ? theme.palette.primary.light :
          error ? theme.palette.warning.light :
          (!recommendations || recommendations.recommendedInspections.length === 0) ? theme.palette.grey[300] :
          theme.palette.success.light
        }`,
        borderRadius: { xs: 1, sm: 2 },
        backgroundColor: 
          loading ? '#f8f9ff' :
          error ? '#fff8e1' :
          (!recommendations || recommendations.recommendedInspections.length === 0) ? '#f5f5f5' :
          '#f0fff4',
        '&:before': {
          display: 'none',
        },
        '&.Mui-expanded': {
          margin: `0 0 ${isMobile ? '12px' : '16px'} 0`,
        }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore sx={{ fontSize: { xs: 20, sm: 24 } }} />}
        sx={{
          minHeight: { xs: 48, sm: 56 },
          '&.Mui-expanded': {
            minHeight: { xs: 48, sm: 56 },
          },
          '& .MuiAccordionSummary-content': {
            margin: { xs: '8px 0', sm: '12px 0' },
            '&.Mui-expanded': {
              margin: { xs: '8px 0', sm: '12px 0' },
            }
          }
        }}
      >
        {getAccordionSummaryContent()}
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0, pb: { xs: 1.5, sm: 2 } }}>
        {loading && (
          <Typography 
            variant="body2" 
            color="primary"
            sx={{ 
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              mb: 1
            }}
          >
            Analyzing inspection requirements for {selectedSubcode.SubcodeName}...
          </Typography>
        )}

        {error && (
          <Alert 
            severity="warning" 
            sx={{ 
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
          >
            <Typography 
              variant="body2"
              sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
            >
              AI analysis unavailable. Please select inspection types manually.
            </Typography>
          </Alert>
        )}

        {!loading && !error && (!recommendations || recommendations.recommendedInspections.length === 0) && (
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
          >
            No specific inspection recommendations available for this subcode.
          </Typography>
        )}

        {!loading && !error && recommendations && recommendations.recommendedInspections.length > 0 && (
          <>
            {/* Recommended Inspections */}
            <Box 
              sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: { xs: 0.5, sm: 1 }, 
                mb: { xs: 1, sm: 1.5 } 
              }}
            >
              {recommendations.recommendedInspections.map((inspection, index) => {
                const chipContent = (
                  <Chip
                    label={
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <span style={{ fontSize: isMobile ? '0.7rem' : '0.8rem' }}>
                          {getPhaseIcon(inspection.phase)}
                        </span>
                        <span style={{ fontSize: isMobile ? '0.7rem' : '0.8rem' }}>
                          {isMobile && inspection.inspectionType.length > 12 
                            ? `${inspection.inspectionType.substring(0, 12)}...` 
                            : inspection.inspectionType
                          }
                        </span>
                      </Box>
                    }
                    size="small"
                    sx={{
                      backgroundColor: `${getPriorityColor(inspection.priority)}15`,
                      border: `1px solid ${getPriorityColor(inspection.priority)}40`,
                      color: getPriorityColor(inspection.priority),
                      fontWeight: 500,
                      height: { xs: 26, sm: 32 },
                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      '& .MuiChip-label': {
                        px: { xs: 1, sm: 1.5 }
                      },
                      '&:hover': {
                        backgroundColor: `${getPriorityColor(inspection.priority)}25`,
                      },
                      ...(isMobile && {
                        minHeight: 32,
                        '&:active': {
                          backgroundColor: `${getPriorityColor(inspection.priority)}35`,
                        }
                      })
                    }}
                  />
                );

                if (isMobile) {
                  return (
                    <Box key={index}>
                      {chipContent}
                    </Box>
                  );
                }

                return (
                  <Tooltip 
                    key={index}
                    title={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {inspection.inspectionType}
                        </Typography>
                        <Typography variant="caption" display="block">
                          Confidence: {Math.round(inspection.confidence * 100)}%
                        </Typography>
                        <Typography variant="caption" display="block">
                          {inspection.reason}
                        </Typography>
                      </Box>
                    }
                    arrow
                    placement={isTablet ? "top" : "bottom"}
                  >
                    {chipContent}
                  </Tooltip>
                );
              })}
            </Box>

            {/* Mobile-specific inspection details */}
            {isMobile && (
              <Box sx={{ mb: 1 }}>
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ 
                    fontStyle: 'italic',
                    fontSize: '0.7rem',
                    display: 'block',
                    mb: 0.5
                  }}
                >
                  Tap for details: {recommendations.recommendedInspections.map(i => 
                    `${i.inspectionType} (${Math.round(i.confidence * 100)}%)`
                  ).join(', ')}
                </Typography>
              </Box>
            )}

            {/* Messages */}
            {recommendations.warningMessages?.length > 0 && (
              <Alert 
                severity="warning" 
                sx={{ 
                  mb: { xs: 0.5, sm: 1 }, 
                  py: { xs: 0.25, sm: 0.5 },
                  '& .MuiAlert-message': {
                    py: { xs: 0.25, sm: 0.5 }
                  }
                }}
              >
                <Typography 
                  variant="caption"
                  sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                >
                  {recommendations.warningMessages.join('. ')}
                </Typography>
              </Alert>
            )}

            {recommendations.infoMessages?.length > 0 && (
              <Alert 
                severity="info" 
                sx={{ 
                  mb: { xs: 0.5, sm: 1 }, 
                  py: { xs: 0.25, sm: 0.5 },
                  '& .MuiAlert-message': {
                    py: { xs: 0.25, sm: 0.5 }
                  }
                }}
              >
                <Typography 
                  variant="caption"
                  sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                >
                  {recommendations.infoMessages.join('. ')}
                </Typography>
              </Alert>
            )}

            {/* Footer */}
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ 
                fontStyle: 'italic',
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                lineHeight: { xs: 1.3, sm: 1.33 },
                display: 'block'
              }}
            >
              💡 Recommendations based on project scope and construction phase. 
              Select inspection types from the dropdown below.
            </Typography>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default AIInspectionRecommendations;